import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { qrPublicService } from '../services/qrPublicService';
import { useAudio } from '../context/AudioContext';
import SelectLanguage from './selectLanguage';
import NoQrView from './noqrView';
import LoadingView from './loadingView';
import Header from './Header';
import DetailAllergy from './DetailAllergy';
import './body.css';
import { environment, withCacheBust } from '../environment';
import logo from '../assets/logo_text.png';
import playStoreLogo from '../assets/img/icon/playStore.png';
import appStoreLogo from '../assets/img/icon/appleStore.png';
import { getUiLocale, translateUiText } from '../i18n';

const getBrowserLanguage = () => (navigator.language || 'en').substring(0, 2).toLowerCase();

const createDetailedError = (code, message, details = {}) => {
  const error = new Error(message);
  error.code = code;
  error.details = details;
  return error;
};

const logDetailedError = (scope, error) => {
  console.group(`❌ [${scope}] ${error.message || 'Unexpected error'}`);
  if (error.code) {
    console.error('Code:', error.code);
  }
  if (error.details) {
    console.error('Details:', error.details);
  }
  if (error.cause) {
    console.error('Cause:', error.cause);
  }
  console.error(error);
  console.groupEnd();
};

const getAssetBasePath = () => (
  environment.takeDataFrom === 'LOCAL' ? '/assets' : environment.assetsUrl
);

const normalizeLookupKey = (value) => String(value ?? '')
  .trim()
  .toLowerCase()
  .replace(/[\s_-]+/g, '');

const buildAssetKeyVariants = (value) => {
  const rawValue = String(value ?? '').trim();

  if (!rawValue) {
    return [];
  }

  return Array.from(new Set([
    rawValue,
    rawValue.toLowerCase(),
    rawValue.replace(/[\s-]+/g, '_'),
    rawValue.replace(/[_-]+/g, ' '),
    rawValue.replace(/[\s_]+/g, '-')
  ].filter(Boolean)));
};

const buildFlagUrl = (languageCode) => `${getAssetBasePath()}/img/flags/${languageCode}.png`;
const buildAllergyImageUrl = (imageKey) => `${getAssetBasePath()}/img/allergies/${imageKey}.png`;
const buildAllergyAudioUrl = (imageKey, languageCode) => (
  `${getAssetBasePath()}/audio/allergies/${imageKey}/${String(languageCode).toLowerCase()}_${imageKey}.mp3`
);
const buildAllergyAudioUrls = (imageKey, languageCode) => (
  buildAssetKeyVariants(imageKey).map((assetKey) => (
    `${getAssetBasePath()}/audio/allergies/${assetKey}/${String(languageCode).toLowerCase()}_${assetKey}.mp3`
  ))
);
const buildAlertAudioUrl = (alertTextId, languageCode) => (
  `${getAssetBasePath()}/audio/alert/${alertTextId}/${String(languageCode).toLowerCase()}_${alertTextId}.mp3`
);

const describeAllergyEntry = (entry) => ({
  id: entry?.id,
  name: entry?.name,
  image: entry?.image
});

const getClosestAllergyMatches = (db, allergyId) => {
  const normalizedRequested = normalizeLookupKey(allergyId);

  if (!normalizedRequested) {
    return [];
  }

  return db
    .filter((item) => {
      const normalizedId = normalizeLookupKey(item?.id);
      const normalizedImage = normalizeLookupKey(item?.image);
      const normalizedName = normalizeLookupKey(item?.name);

      return (
        normalizedId.includes(normalizedRequested) ||
        normalizedRequested.includes(normalizedId) ||
        normalizedImage.includes(normalizedRequested) ||
        normalizedRequested.includes(normalizedImage) ||
        normalizedName.includes(normalizedRequested) ||
        normalizedRequested.includes(normalizedName)
      );
    })
    .slice(0, 5)
    .map(describeAllergyEntry);
};

const findAllergyEntry = (db, allergyId, sourceLabel) => {
  const exactMatch = db.find((item) => (
    String(item?.id) === String(allergyId) ||
    item?.image === allergyId ||
    item?.name === allergyId
  ));

  if (exactMatch) {
    return exactMatch;
  }

  const normalizedRequested = normalizeLookupKey(allergyId);
  const normalizedMatch = db.find((item) => (
    normalizeLookupKey(item?.id) === normalizedRequested ||
    normalizeLookupKey(item?.image) === normalizedRequested ||
    normalizeLookupKey(item?.name) === normalizedRequested
  ));

  if (normalizedMatch) {
    console.warn('⚠️ [Allergy Lookup] Resolved allergy through normalized fallback match.', {
      source: sourceLabel,
      requestedAllergyId: allergyId,
      normalizedRequestedKey: normalizedRequested,
      resolvedEntry: describeAllergyEntry(normalizedMatch)
    });
  }

  return normalizedMatch || null;
};

const fetchJsonAsset = async (url, label, sourceLabel) => {
  console.log(`🌐 [Asset Fetch] Requesting ${label}`, {
    source: sourceLabel,
    url
  });

  const response = await fetch(url);

  console.log(`📥 [Asset Fetch] Response received for ${label}`, {
    source: sourceLabel,
    url,
    status: response.status,
    ok: response.ok,
    contentType: response.headers.get('content-type')
  });

  if (!response.ok) {
    throw createDetailedError(
      'ASSET_FETCH_FAILED',
      `Failed to fetch ${label}.`,
      {
        source: sourceLabel,
        url,
        status: response.status,
        statusText: response.statusText
      }
    );
  }

  try {
    const data = await response.json();

    console.log(`✅ [Asset Fetch] Parsed ${label}`, {
      source: sourceLabel,
      url,
      recordCount: Array.isArray(data) ? data.length : undefined,
      shape: Array.isArray(data) ? 'array' : typeof data
    });

    return data;
  } catch (error) {
    throw createDetailedError(
      'ASSET_JSON_INVALID',
      `Failed to parse ${label} as JSON.`,
      {
        source: sourceLabel,
        url,
        cause: error.message
      }
    );
  }
};

const resolveCardAllergyId = (entry, index) => {
  if (typeof entry === 'string' || typeof entry === 'number') {
    return entry;
  }

  const allergyId = entry?.allergyId ?? entry?.id ?? entry?.image ?? null;

  if (allergyId === null || allergyId === undefined || allergyId === '') {
    throw createDetailedError(
      'QR_ALLERGY_REFERENCE_INVALID',
      'Encountered an allergy reference without an id.',
      { index, entry }
    );
  }

  return allergyId;
};

const mapAllergiesFromDb = (db, allergyIds, languageCode, sourceLabel) => {
  if (!Array.isArray(db)) {
    throw createDetailedError('ALLERGIES_DB_INVALID', 'Allergies JSON is not an array.', {
      source: sourceLabel
    });
  }

  if (!Array.isArray(allergyIds)) {
    throw createDetailedError('CARD_ALLERGIES_INVALID', 'Card allergy references are not an array.', {
      source: sourceLabel,
      allergyIds
    });
  }

  console.log('🧩 [Allergy Mapping] Resolving card allergy references.', {
    source: sourceLabel,
    requestedLanguage: languageCode,
    requestedAllergyIds: allergyIds,
    allergyDbCount: db.length,
    allergyDbSample: db.slice(-10).map(describeAllergyEntry)
  });

  return allergyIds.map((rawId, index) => {
    const allergyId = resolveCardAllergyId(rawId, index);
    const allergyEntry = findAllergyEntry(db, allergyId, sourceLabel);

    if (!allergyEntry) {
      throw createDetailedError(
        'ALLERGY_NOT_FOUND',
        `Allergy "${allergyId}" was not found in allergies.json.`,
        {
          source: sourceLabel,
          allergyId,
          index,
          rawReference: rawId,
          normalizedRequestedKey: normalizeLookupKey(allergyId),
          closeMatches: getClosestAllergyMatches(db, allergyId)
        }
      );
    }

    if (!allergyEntry.image) {
      throw createDetailedError(
        'ALLERGY_IMAGE_KEY_MISSING',
        `Allergy "${allergyEntry.name || allergyId}" is missing its image key.`,
        { source: sourceLabel, allergyId, allergyEntry }
      );
    }

    const translatedLabel = allergyEntry.translation?.[languageCode] || allergyEntry.translation?.en || allergyEntry.name;

    if (!translatedLabel) {
      throw createDetailedError(
        'ALLERGY_TRANSLATION_MISSING',
        `Allergy "${allergyEntry.image}" has no translation for "${languageCode}" or fallback English label.`,
        { source: sourceLabel, allergyId, availableTranslations: Object.keys(allergyEntry.translation || {}) }
      );
    }

    if (!allergyEntry.translation?.[languageCode]) {
      console.warn('⚠️ [Allergy] Missing translation for requested language, falling back to English/name.', {
        source: sourceLabel,
        allergyId,
        imageKey: allergyEntry.image,
        requestedLanguage: languageCode
      });
    }

    const audioUrls = buildAllergyAudioUrls(allergyEntry.image, languageCode);

    if (audioUrls.length > 1 && /[\s_-]/.test(String(allergyEntry.image))) {
      console.info('🔊 [Allergy Audio] Generated multiple audio path candidates for allergy key.', {
        source: sourceLabel,
        allergyId,
        imageKey: allergyEntry.image,
        primaryAudioUrl: audioUrls[0],
        fallbackAudioUrls: audioUrls.slice(1)
      });
    }

    return {
      id: allergyEntry.id,
      name: allergyEntry.name || String(allergyId),
      translation: translatedLabel,
      imgUrl: buildAllergyImageUrl(allergyEntry.image),
      audioUrl: audioUrls[0] || buildAllergyAudioUrl(allergyEntry.image, languageCode),
      audioFallbackUrls: audioUrls.slice(1),
      imageKey: allergyEntry.image
    };
  });
};

const mapAlertData = (titles, alerts, alertTitleId, alertTextId, languageCode, sourceLabel) => {
  const titleObj = titles.find((item) => String(item?.id) === String(alertTitleId));
  if (!titleObj) {
    throw createDetailedError('ALERT_TITLE_NOT_FOUND', `Alert title "${alertTitleId}" was not found.`, {
      source: sourceLabel,
      alertTitleId
    });
  }

  const alertObj = alerts.find((item) => String(item?.id) === String(alertTextId));
  if (!alertObj) {
    throw createDetailedError('ALERT_TEXT_NOT_FOUND', `Alert text "${alertTextId}" was not found.`, {
      source: sourceLabel,
      alertTextId
    });
  }

  const title = titleObj.translation?.[languageCode] || titleObj.translation?.en;
  const text = alertObj.translation?.[languageCode] || alertObj.translation?.en;

  if (!title) {
    throw createDetailedError(
      'ALERT_TITLE_TRANSLATION_MISSING',
      `Alert title "${alertTitleId}" has no translation for "${languageCode}".`,
      { source: sourceLabel, alertTitleId }
    );
  }

  if (!text) {
    throw createDetailedError(
      'ALERT_TEXT_TRANSLATION_MISSING',
      `Alert text "${alertTextId}" has no translation for "${languageCode}".`,
      { source: sourceLabel, alertTextId }
    );
  }

  return {
    title,
    text,
    audioUrl: buildAlertAudioUrl(alertTextId, languageCode)
  };
};

const getQrErrorReasonFromError = (error) => {
  const errorCode = String(error?.code || error?.message || '');

  if (errorCode.includes('SESSION_EXPIRED')) {
    console.error('   - Reason: Session has expired, user must rescan QR code');
    return 'session_expired';
  }
  if (errorCode.includes('SESSION_INVALID')) {
    console.error('   - Reason: Invalid or missing session token');
    return 'api_error';
  }
  if (errorCode.includes('SESSION_REQUIRED')) {
    console.error('   - Reason: Session required but not provided');
    return 'api_error';
  }
  if (errorCode.includes('QR_NOT_FOUND') || errorCode.includes('404')) {
    console.error('   - Reason: QR code not found in database');
    return 'not_found';
  }
  if (errorCode.includes('QR_DISABLED')) {
    console.error('   - Reason: QR code has been disabled by owner');
    return 'disabled';
  }
  if (errorCode.includes('QR_EXPIRED')) {
    console.error('   - Reason: QR code has permanently expired');
    return 'expired';
  }
  if (errorCode.includes('QR_NO_CARD') || errorCode.includes('cardId')) {
    console.error('   - Reason: QR code exists but no card is linked');
    return 'no_card';
  }
  if (
    errorCode.includes('ALLERGY') ||
    errorCode.includes('ALERT') ||
    errorCode.includes('CARD_ALLERGIES_INVALID') ||
    errorCode.includes('QR_ALLERGY_REFERENCE_INVALID')
  ) {
    console.error('   - Reason: Error while validating allergy or alert data');
    return 'data_error';
  }

  console.error('   - Reason: General API/network error');
  return 'api_error';
};

function Body() {
  const { qrCodeId: qrCodeIdFromRoute } = useParams();
  const [cardData, setCardData] = useState(null);
  const [allergies, setAllergies] = useState([]);
  const [alert, setAlert] = useState(null);
  const [gridColumns, setGridColumns] = useState(3);
  const [flagUrl, setFlagUrl] = useState(null);
  const [userLanguageBrowser, setUserLanguage] = useState(getBrowserLanguage());
  const [loading, setLoading] = useState(true);
  const [showPage, setShowPage] = useState();
  const [qrErrorReason, setQrErrorReason] = useState(null);
  const [audioMain, setAudioMain] = useState('');
  const [sessionExpiresAt, setSessionExpiresAt] = useState(null);
  const [iconAssets, setIconAssets] = useState({});
  const [showModalSelectLanguage, setShowModalLanguage] = useState(false);
  const [languageList, setLanguageList] = useState([]);
  const [showRealImages, setShowRealImages] = useState(false);
  const [selectedAllergy, setSelectedAllergy] = useState(null);

  const { playSound } = useAudio();
  const uiLocale = getUiLocale(userLanguageBrowser);

  useEffect(() => {
    const length = allergies.length;

    if (length === 1) {
      setGridColumns(1);
    } else if (length >= 2 && length <= 4) {
      setGridColumns(2);
    } else if (length > 4) {
      setGridColumns(3);
    }
  }, [allergies]);

  const getAssets = async () => {
    const basePath = getAssetBasePath();
    setIconAssets({
      speaker: `${basePath}/img/icon/speaker.png`,
      warning: `${basePath}/img/icon/warning_yellow.png`,
      tableSet: `${basePath}/img/icon/tableSet.png`,
      picture: `${basePath}/img/icon/picture.png`
    });
  };

  const getAllergyImageUrl = (item) => {
    if (!item?.imgUrl) {
      return '';
    }

    const baseName = item.imgUrl.replace('.png', '');
    return showRealImages ? `${baseName}_real.png` : item.imgUrl;
  };

  const handleAllergyImageError = (event, allergy) => {
    const failedUrl = event?.currentTarget?.currentSrc || event?.currentTarget?.src;

    if (showRealImages && failedUrl?.endsWith('_real.png') && allergy?.imgUrl) {
      console.error('❌ [Allergy Image] Real image not found, falling back to icon image.', {
        allergyId: allergy?.id,
        allergyName: allergy?.name,
        imageKey: allergy?.imageKey,
        attemptedUrl: failedUrl
      });
      event.currentTarget.onerror = null;
      event.currentTarget.src = allergy.imgUrl;
      return;
    }

    console.error('❌ [Allergy Image] Allergy image could not be loaded.', {
      allergyId: allergy?.id,
      allergyName: allergy?.name,
      imageKey: allergy?.imageKey,
      attemptedUrl: failedUrl
    });
  };

  const fetchAllergiesFromVps = async (allergyIds, languageCode) => {
    const db = await fetchJsonAsset(
      withCacheBust(`${environment.assetsUrl}/json/allergies.json`),
      'allergies.json',
      'VPS'
    );
    return mapAllergiesFromDb(db, allergyIds, languageCode, 'VPS');
  };

  const fetchAlertFromVps = async (alertTitleId, alertTextId, languageCode) => {
    const titles = await fetchJsonAsset(
      withCacheBust(`${environment.assetsUrl}/json/alerts_title.json`),
      'alerts_title.json',
      'VPS'
    );

    const alerts = await fetchJsonAsset(
      withCacheBust(`${environment.assetsUrl}/json/alerts.json`),
      'alerts.json',
      'VPS'
    );

    return mapAlertData(titles, alerts, alertTitleId, alertTextId, languageCode, 'VPS');
  };

  const fetchAllergiesFromLocal = async (allergyIds, languageCode) => {
    const db = await fetchJsonAsset(
      withCacheBust('/assets/json/allergies.json'),
      'allergies.json',
      'LOCAL'
    );
    return mapAllergiesFromDb(db, allergyIds, languageCode, 'LOCAL');
  };

  const fetchAlertsFromLocal = async (alertTitleId, alertTextId, languageCode) => {
    const titles = await fetchJsonAsset(
      withCacheBust('/assets/json/alerts_title.json'),
      'alerts_title.json',
      'LOCAL'
    );

    const alerts = await fetchJsonAsset(
      withCacheBust('/assets/json/alerts.json'),
      'alerts.json',
      'LOCAL'
    );

    return mapAlertData(titles, alerts, alertTitleId, alertTextId, languageCode, 'LOCAL');
  };

  const fetchLanguagesFromLocal = async () => {
    const languages = await fetchJsonAsset(
      withCacheBust('/assets/json/languages.json'),
      'languages.json',
      'LOCAL'
    );

    return languages.map((language) => ({
      id: language.id,
      language: language.language,
      name: language.name,
      languageTranslation: language.languageTranslation,
      imgUrl: `/assets/img/flags/${language.language}.png`,
      translationCardTitle: language.translationCardTitle,
      translation: language.translation
    }));
  };

  const fetchLanguagesFromVps = async () => {
    const languages = await fetchJsonAsset(
      withCacheBust(`${environment.assetsUrl}/json/languages.json`),
      'languages.json',
      'VPS'
    );

    return languages.map((language) => ({
      id: language.id,
      language: language.language,
      name: language.name,
      languageTranslation: language.languageTranslation,
      imgUrl: `${environment.assetsUrl}/img/flags/${language.language}.png`,
      translationCardTitle: language.translationCardTitle,
      translation: language.translation
    }));
  };

  useEffect(() => {
    const fetchQrAndCard = async () => {
      let qrCodeId = qrCodeIdFromRoute;

      if (!qrCodeId) {
        const urlParams = new URLSearchParams(window.location.search);
        qrCodeId = urlParams.get('qrCode');
      }

      console.log('QR Code ID from URL:', qrCodeId);

      if (!qrCodeId) {
        setShowPage(false);
        setLoading(false);
        return;
      }

      try {
        console.log('🔍 Fetching QR code data from API...');
        const publicQrData = await qrPublicService.getPublicQrCode(qrCodeId);
        console.log('✅ Public QR Data received:', publicQrData);

        if (publicQrData.disabled === true) {
          setQrErrorReason('disabled');
          setShowPage(false);
          return;
        }

        if (publicQrData.expired === true) {
          setQrErrorReason('expired');
          setShowPage(false);
          return;
        }

        if (!publicQrData.cardId) {
          console.error('❌ [Body] No card linked to this QR code.');
          setQrErrorReason('no_card');
          setShowPage(false);
          return;
        }

        const normalizedSessionAllergies = Array.isArray(publicQrData.allergies)
          ? publicQrData.allergies
          : (() => {
              throw createDetailedError(
                'CARD_ALLERGIES_INVALID',
                'Session payload did not include a valid allergies array.',
                { allergies: publicQrData.allergies }
              );
            })();

        console.log('🧾 [Body] QR session allergy references received from API.', {
          qrCodeId,
          cardId: publicQrData.cardId,
          allergies: normalizedSessionAllergies.map((entry, index) => ({
            index,
            rawEntry: entry,
            resolvedReference: resolveCardAllergyId(entry, index),
            type: typeof entry
          }))
        });

        const allergyIds = normalizedSessionAllergies.map((entry, index) => resolveCardAllergyId(entry, index));

        const card = {
          id: publicQrData.cardId,
          cardName: publicQrData.cardName,
          description: publicQrData.cardDescription,
          languageSelectedId: publicQrData.languageSelectedId,
          alertAllergyId: publicQrData.alertAllergyId,
          alertAllergyTitleId: publicQrData.alertAllergyTitleId,
          allergyIds,
          allergies: normalizedSessionAllergies,
          pictureTemplateUrl: null
        };

        if (publicQrData.sessionExpiresAt) {
          setSessionExpiresAt(new Date(publicQrData.sessionExpiresAt));
        } else {
          setSessionExpiresAt(null);
        }

        setCardData(card);
        console.log('Card Data:', card);

        const languageCode = getBrowserLanguage();
        setUserLanguage(languageCode);
        setFlagUrl(buildFlagUrl(languageCode));

        if (environment.takeDataFrom === 'LOCAL') {
          const localAllergies = await fetchAllergiesFromLocal(card.allergyIds, languageCode);
          setAllergies(localAllergies);

          const alertInfo = await fetchAlertsFromLocal(
            card.alertAllergyTitleId,
            card.alertAllergyId,
            languageCode
          );
          setAlert(alertInfo);
          setAudioMain(alertInfo.audioUrl);
        } else {
          const remoteAllergies = await fetchAllergiesFromVps(card.allergyIds, languageCode);
          setAllergies(remoteAllergies);

          const alertInfo = await fetchAlertFromVps(
            card.alertAllergyTitleId,
            card.alertAllergyId,
            languageCode
          );
          setAlert(alertInfo);
          setAudioMain(alertInfo.audioUrl);
        }

        await getAssets();

        if (environment.takeDataFrom === 'LOCAL') {
          setLanguageList(await fetchLanguagesFromLocal());
        } else {
          setLanguageList(await fetchLanguagesFromVps());
        }

        setShowPage(true);
      } catch (error) {
        logDetailedError('Body', error);
        setQrErrorReason(getQrErrorReasonFromError(error));
        setShowPage(false);
      } finally {
        setLoading(false);
      }
    };

    fetchQrAndCard();
  }, [qrCodeIdFromRoute]);

  const handlePlaySound = () => {
    console.log('🔊 [Alert Audio] Attempting alert audio playback.', {
      url: audioMain
    });
    playSound(audioMain);
  };

  const openModalLanguage = () => {
    setShowModalLanguage(true);
  };

  const closeModalLanguage = () => {
    setShowModalLanguage(false);
  };

  const fetchAudioUrl = async (languageCode) => {
    try {
      if (!cardData) {
        return;
      }

      if (environment.takeDataFrom === 'LOCAL') {
        const alertInfo = await fetchAlertsFromLocal(
          cardData.alertAllergyTitleId,
          cardData.alertAllergyId,
          languageCode
        );
        setAlert(alertInfo);
        setAudioMain(alertInfo.audioUrl);

        const updatedAllergies = await fetchAllergiesFromLocal(cardData.allergyIds, languageCode);
        setAllergies(updatedAllergies);
      } else {
        const alertInfo = await fetchAlertFromVps(
          cardData.alertAllergyTitleId,
          cardData.alertAllergyId,
          languageCode
        );
        setAlert(alertInfo);
        setAudioMain(alertInfo.audioUrl);

        const updatedAllergies = await fetchAllergiesFromVps(cardData.allergyIds, languageCode);
        setAllergies(updatedAllergies);
      }
    } catch (error) {
      logDetailedError('Language change', error);
    }
  };

  const handleSelectLanguage = async (languageSelected) => {
    const newLang = languageSelected.language;
    setUserLanguage(newLang);
    setFlagUrl(buildFlagUrl(newLang));
    await fetchAudioUrl(newLang);
    closeModalLanguage();
  };

  const toggleImageType = () => {
    setShowRealImages(!showRealImages);
  };

  const openAllergyDetail = (allergy) => {
    console.log('🪪 [Allergy Detail] Opening allergy detail.', {
      allergyId: allergy?.id,
      allergyName: allergy?.name,
      imageKey: allergy?.imageKey,
      audioUrl: allergy?.audioUrl,
      audioFallbackUrls: allergy?.audioFallbackUrls || []
    });
    setSelectedAllergy(allergy);
  };

  const closeAllergyDetail = () => {
    setSelectedAllergy(null);
  };

  if (loading) {
    return <LoadingView locale={uiLocale} />;
  }

  if (showPage === false) {
    return <NoQrView reason={qrErrorReason} locale={uiLocale} />;
  }

  if (!showPage) {
    return null;
  }

  return (
    <div className='page-content'>
      <Header
        title={alert?.title}
        leftIcon={iconAssets?.warning}
        rightIcon={flagUrl}
        onRightIconClick={openModalLanguage}
        locale={uiLocale}
      />

      <div className='card-container'>
        <div className="allergy-list">
          <div
            className='allergy_list_containter'
            style={{ gridTemplateColumns: `repeat(${gridColumns}, 1fr)` }}
          >
            {allergies.map((item, index) => (
              <div className='allergy_item_wrapper' key={index}>
                <div
                  className='allergy_list_content'
                  onClick={() => openAllergyDetail(item)}
                >
                  <div className="img_container">
                    <img
                      className='allergy_img'
                      src={getAllergyImageUrl(item)}
                      alt={item.name}
                      onError={(event) => handleAllergyImageError(event, item)}
                    />
                  </div>

                  <p className='allergy_title'>{item.translation}</p>
                  {userLanguageBrowser !== 'en' && (
                    <p className='allergy_title_english'>{item.name}</p>
                  )}
                </div>

                <button
                  className={`allergy_speaker_btn ${!item.audioUrl ? 'disabled' : ''}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (item.audioUrl) {
                      console.log('🔊 [Allergy Audio] Attempting allergy audio playback.', {
                        allergyId: item.id,
                        allergyName: item.name,
                        imageKey: item.imageKey,
                        audioUrl: item.audioUrl,
                        audioFallbackUrls: item.audioFallbackUrls || []
                      });
                      playSound(item.audioUrl);
                    }
                  }}
                  disabled={!item.audioUrl}
                >
                  {iconAssets.speaker && (
                    <img
                      className='allergy_speaker_icon'
                      src={iconAssets.speaker}
                      alt={translateUiText(uiLocale, 'playSoundAlt')}
                    />
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="separator-line"></div>

        <div className='alert-text-section'>
          <p className='alert-text'>{alert?.text}</p>
        </div>

        <div className="action-bar">
          <div className="action-item" onClick={toggleImageType}>
            <div className={`action-icon-circle ${showRealImages ? 'active' : ''}`}>
              {iconAssets.picture && (
                <img className="action-icon" src={iconAssets.picture} alt={translateUiText(uiLocale, 'imageToggleAlt')} />
              )}
            </div>
            <span className="action-label">
              {translateUiText(uiLocale, 'switchTo')}
              <br />
              {showRealImages ? translateUiText(uiLocale, 'icons') : translateUiText(uiLocale, 'images')}
            </span>
          </div>

          <div className="action-item" onClick={openModalLanguage}>
            <div className="action-icon-circle flag-circle">
              <img className="action-flag" src={flagUrl} alt={translateUiText(uiLocale, 'flagAlt')} />
            </div>
            <span className="action-label">
              {translateUiText(uiLocale, 'tapTo')}
              <br />
              {translateUiText(uiLocale, 'changeLanguage')}
            </span>
          </div>

          <div
            className={`action-item ${!audioMain ? 'disabled' : ''}`}
            onClick={audioMain ? handlePlaySound : undefined}
          >
            <div className={`action-icon-circle ${!audioMain ? 'disabled' : ''}`}>
              {iconAssets.speaker && (
                <img className="action-icon" src={iconAssets.speaker} alt={translateUiText(uiLocale, 'speakerAlt')} />
              )}
            </div>
            <span className="action-label">
              {translateUiText(uiLocale, 'tapToHear')}
              <br />
              {translateUiText(uiLocale, 'theText')}
            </span>
          </div>
        </div>

        {sessionExpiresAt && (
          <div className="session-notice">
            <p className="session-notice-text">
              {translateUiText(uiLocale, 'accessValidUntil', {
                time: sessionExpiresAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
              })}
            </p>
            <p className="session-notice-hint">{translateUiText(uiLocale, 'reloadHint')}</p>
          </div>
        )}

        <div className="card-footer">
          <div className="card-footer-content">
            <img src={logo} alt={translateUiText(uiLocale, 'foodAllergyCardAlt')} className="card-footer-logo" />
            <div className="card-footer-download">
              <p>{translateUiText(uiLocale, 'downloadApp')}</p>
              <a href="https://play.google.com/store/apps/details?id=com.foodallergycard" target="_blank" rel="noopener noreferrer">
                <img src={playStoreLogo} alt={translateUiText(uiLocale, 'playStoreAlt')} className="card-footer-store" />
              </a>
              <a href="https://apps.apple.com/app/food-allergy-card/id6448350429" target="_blank" rel="noopener noreferrer">
                <img src={appStoreLogo} alt={translateUiText(uiLocale, 'appStoreAlt')} className="card-footer-store" />
              </a>
            </div>
          </div>
          <p className="card-footer-copyright">
            &copy; {new Date().getFullYear()} Food Allergy Card. {translateUiText(uiLocale, 'allRightsReserved')}
          </p>
        </div>
      </div>

      {showModalSelectLanguage && (
        <SelectLanguage
          title={translateUiText(uiLocale, 'selectLanguageTitle')}
          onClose={closeModalLanguage}
          onSelectLanguage={handleSelectLanguage}
          languages={languageList}
          userLanguageBrowser={userLanguageBrowser}
          locale={uiLocale}
        />
      )}

      {selectedAllergy && (
        <DetailAllergy
          allergy={selectedAllergy}
          title={alert?.title}
          userLanguageBrowser={userLanguageBrowser}
          locale={uiLocale}
          flagUrl={flagUrl}
          iconAssets={iconAssets}
          showRealImages={showRealImages}
          onClose={closeAllergyDetail}
          onPlaySound={playSound}
          getAllergyImageUrl={getAllergyImageUrl}
          onImageError={handleAllergyImageError}
        />
      )}
    </div>
  );
}

export default Body;
